import { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Modal, Select, Space } from "antd";
import { toast } from "react-toastify";
import { OLIVEEB_APP_OPTIONS, RBAC_RESOURCE_TYPE_OPTIONS } from "constants";

export default function CreateOrUpdateRoleModal({
  show,
  onHide,
  onSave,
  role,
}) {
  const isUpdate = Boolean(role);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const initialValues = useMemo(
    () => ({
      name: role?.name || "",
      owner: role?.owner || "",
      type: role?.type || "SYSTEM",
      description: role?.description || "",
    }),
    [role]
  );

  useEffect(() => {
    if (show) {
      form.setFieldsValue(initialValues);
    } else {
      form.resetFields();
    }
  }, [form, initialValues, show]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const owner = String(values.owner || "").trim();
      const type = String(values.type || "").trim();
      await onSave(
        {
          name: String(values.name || "").trim(),
          owner,
          type,
          description: String(values.description || "").trim(),
        },
        role?.id
      );

      onHide();
    } catch (err) {
      if (err?.errorFields) return;
      toast.error(err?.message || "Failed to save role");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={show}
      onCancel={onHide}
      centered
      destroyOnClose
      zIndex={1300}
      title={isUpdate ? "Update Role" : "Add Role"}
      footer={
        <Space className="w-100 justify-content-end">
          <Button onClick={onHide}>Cancel</Button>
          <Button type="primary" onClick={handleSubmit} loading={submitting}>
            {isUpdate ? "Update" : "Save"}
          </Button>
        </Space>
      }
      styles={{
        body: { paddingTop: 8 },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        requiredMark={false}
      >
        <Form.Item
          label="Role Name"
          name="name"
          rules={[{ required: true, message: "Role name is required" }]}
        >
          <Input placeholder="Enter role name" autoComplete="off" />
        </Form.Item>

        <Form.Item
          label="Owner"
          name="owner"
          rules={[{ required: true, message: "Owner app is required" }]}
        >
          <Select
            showSearch
            allowClear
            placeholder="Select owner app"
            optionFilterProp="children"
            filterOption={(input, option) =>
              String(option?.children || "")
                .toLowerCase()
                .includes(String(input || "").toLowerCase())
            }
          >
            {OLIVEEB_APP_OPTIONS.map((opt) => (
              <Select.Option key={opt.label} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Type"
          name="type"
          rules={[{ required: true, message: "Type is required" }]}
        >
          <Select
            showSearch
            allowClear
            placeholder="Select resource type"
            optionFilterProp="children"
            filterOption={(input, option) =>
              String(option?.children || "")
                .toLowerCase()
                .includes(String(input || "").toLowerCase())
            }
          >
            {RBAC_RESOURCE_TYPE_OPTIONS.map((opt) => (
              <Select.Option key={opt.value} value={opt.value}>
                {opt.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Description" name="description">
          <Input.TextArea rows={3} placeholder="Short description for this role" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
